import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, User, Link2, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function JoinWorkspacePage() {
  const { user, profile, refreshProfile } = useAuth();
  const [invite, setInvite] = useState<any>(null);
  const [fullName, setFullName] = useState("");
  const [preferredName, setPreferredName] = useState("");
  const [personalIntention, setPersonalIntention] = useState("");
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  // Code-based join
  const [joinCode, setJoinCode] = useState("");
  const [joiningByCode, setJoiningByCode] = useState(false);

  useEffect(() => {
    if (!profile?.email) return;
    supabase.from("workspace_invites").select("*").eq("invited_email", profile.email.toLowerCase()).eq("accepted", false).limit(1).single()
      .then(({ data }) => { setInvite(data); setLoading(false); });
  }, [profile?.email]);

  const handleJoin = async () => {
    if (!invite || !fullName.trim()) return;
    setJoining(true);
    try {
      const { error: profErr } = await supabase.from("profiles").update({
        workspace_id: invite.workspace_id, full_name: fullName.trim(), preferred_name: preferredName.trim() || null, personal_intention: personalIntention.trim() || null, onboarding_complete: true,
      }).eq("user_id", user!.id);
      if (profErr) throw profErr;
      await supabase.from("workspace_invites").update({ accepted: true }).eq("id", invite.id);
      await refreshProfile();
      toast.success("You're in! Welcome to your shared space 💛");
    } catch (err: any) {
      toast.error("Something went wrong", { description: err.message });
    } finally {
      setJoining(false);
    }
  };

  const handleJoinByCode = async () => {
    if (!joinCode.trim() || !fullName.trim()) { toast.error("Please enter your name and code"); return; }
    setJoiningByCode(true);
    try {
      const { data, error } = await supabase.functions.invoke("validate-workspace-code", {
        body: {
          code: joinCode.trim(),
          action: "join",
          full_name: fullName.trim(),
          preferred_name: preferredName.trim() || null,
          personal_intention: personalIntention.trim() || null,
        },
      });
      if (error) throw error;
      if (!data?.valid) { toast.error(data?.error || "Invalid or expired code"); setJoiningByCode(false); return; }
      await refreshProfile();
      toast.success("You're in! Welcome 💛");
    } catch (err: any) {
      toast.error("Something went wrong", { description: err.message });
    } finally {
      setJoiningByCode(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Checking for invitations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 overflow-x-hidden">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <Heart className="w-7 h-7 text-accent mx-auto mb-3" fill="currentColor" />
          <h1 className="font-display text-2xl font-semibold text-foreground">
            {invite ? "You've been invited" : "Join your partner"}
          </h1>
          <p className="text-muted-foreground mt-2">
            {invite ? "Your partner is waiting for you." : "Enter the linking code your partner shared with you."}
          </p>
        </div>

        <div className="bg-card border border-border rounded-xl p-8 space-y-6">
          <div className="flex items-center gap-2 text-accent"><User className="w-5 h-5" /><span className="text-sm font-medium">About you</span></div>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Your name</label>
              <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Full name" className="w-full bg-background border border-input rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Preferred name <span className="text-muted-foreground">(optional)</span></label>
              <input value={preferredName} onChange={(e) => setPreferredName(e.target.value)} placeholder="Nickname" className="w-full bg-background border border-input rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Your intention <span className="text-muted-foreground">(optional)</span></label>
              <textarea value={personalIntention} onChange={(e) => setPersonalIntention(e.target.value)} placeholder="What do you hope this brings?" className="w-full bg-background border border-input rounded-lg p-3 text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
          </div>

          {invite ? (
            <button onClick={handleJoin} disabled={joining || !fullName.trim()} className="w-full py-3 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50">
              {joining ? "Joining..." : "Join Together"} 💛
            </button>
          ) : (
            <div className="space-y-4 border-t border-border pt-4">
              <div className="flex items-center gap-2"><Link2 className="w-4 h-4 text-muted-foreground" /><label className="text-sm font-medium text-foreground">Enter linking code</label></div>
              <div className="flex gap-2">
                <input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="6-character code" className="flex-1 min-w-0 bg-background border border-input rounded-lg px-3 py-2.5 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-ring" maxLength={6} />
                <button onClick={handleJoinByCode} disabled={joiningByCode || !joinCode.trim() || !fullName.trim()} className="px-4 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium disabled:opacity-50 flex-shrink-0">
                  {joiningByCode ? <Loader2 className="w-4 h-4 animate-spin" /> : "Join"}
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
